import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Package, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState("checking"); // checking | paid | error | expired
  const [order, setOrder] = useState(null);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setOrder(data.order);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts >= 6) { setStatus("error"); return; }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= 6) { setStatus("error"); return; }
        attempts += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId, clear]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="checkout-success-page">
      {status === "checking" && (
        <div data-testid="status-checking">
          <Loader2 className="w-14 h-14 mx-auto animate-spin" />
          <h1 className="font-display font-bold text-3xl mt-6">Confirming your payment…</h1>
          <p className="font-semibold text-ink/70 mt-2">Hang tight, this only takes a moment.</p>
        </div>
      )}

      {status === "paid" && (
        <div data-testid="status-paid">
          <div className="w-20 h-20 mx-auto bg-brand-green neo rounded-2xl grid place-items-center"><CheckCircle2 className="w-10 h-10" /></div>
          <h1 className="font-display font-bold text-4xl mt-6">Order confirmed! 🎉</h1>
          <p className="font-semibold text-ink/70 mt-3">Thanks for fabricating with us. Your tees are being printed.</p>
          {order && (
            <div className="bg-white neo rounded-2xl p-6 mt-8 text-left">
              <div className="flex items-center gap-2 font-bold mb-3"><Package className="w-5 h-5" /> Order {order.order_id}</div>
              <div className="space-y-2">
                {order.items.map((it, i) => (
                  <div key={i} className="flex justify-between font-semibold text-sm">
                    <span>{it.name} · {it.color}/{it.size} × {it.quantity}</span>
                    <span>${it.line_total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t-2 border-ink mt-3 pt-3 flex justify-between font-display font-bold text-lg">
                <span>Total</span><span>${order.amount.toFixed(2)}</span>
              </div>
            </div>
          )}
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/account" className="bg-ink text-paper font-bold px-6 py-3.5 rounded-xl neo-press border-2 border-ink inline-flex items-center gap-2">My orders <ArrowRight className="w-5 h-5" /></Link>
            <Link to="/shop" className="bg-brand-sky font-bold px-6 py-3.5 rounded-xl neo-press border-2 border-ink">Keep shopping</Link>
          </div>
        </div>
      )}

      {(status === "error" || status === "expired") && (
        <div data-testid="status-error">
          <div className="w-20 h-20 mx-auto bg-brand-pink neo rounded-2xl grid place-items-center"><XCircle className="w-10 h-10" /></div>
          <h1 className="font-display font-bold text-3xl mt-6">{status === "expired" ? "Payment session expired" : "We couldn't confirm your payment"}</h1>
          <p className="font-semibold text-ink/70 mt-2">If you were charged, contact support. Otherwise, try again.</p>
          <Link to="/cart" className="mt-8 inline-flex bg-ink text-paper font-bold px-6 py-3.5 rounded-xl neo-press border-2 border-ink">Back to cart</Link>
        </div>
      )}
    </div>
  );
}
